import { AxiosHeaders } from 'axios'
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { jwtDecode } from 'jwt-decode'

import { clearSavedTokens, getRefreshToken, saveTokens } from '../authentication'

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Opts a request out of the refresh-on-401 dance and the forced sign-out that
     * follows a failed refresh. For best-effort background calls (e.g. syncing the
     * account's locale) whose failure must stay silent and local — a stray 401 on
     * one of these must never log an otherwise-valid session out from under the user.
     */
    skipAuthRefresh?: boolean
  }
}

export type AuthenticationTokens = {
  access_token: string
  refresh_token: string
}

export type ExchangeTokenFn = (refreshToken: string) => Promise<AuthenticationTokens>

const isOAuthTokenEndpointRequest = (config: { url?: string }): boolean => {
  const url = config.url ?? ''
  return url.includes('/oauth/token')
}

const EXPIRY_BUFFER_SECONDS = 60

const isAccessTokenExpiredOrExpiringSoon = (accessToken: string): boolean => {
  try {
    const decoded = jwtDecode<{ exp?: number }>(accessToken)
    if (decoded.exp == null) {
      return true
    }
    return decoded.exp <= Math.floor(Date.now() / 1000) + EXPIRY_BUFFER_SECONDS
  } catch {
    return true
  }
}

export const setupTokenRefresh = (
  apiClient: AxiosInstance,
  exchangeTokenFn: ExchangeTokenFn,
  onUnauthenticated: () => void,
): void => {
  let refreshPromise: Promise<string> | null = null

  const tryRefreshAndGetNewAccessToken = async (): Promise<string> => {
    if (refreshPromise) {
      return refreshPromise
    }

    const promise = (async (): Promise<string> => {
      const refreshToken = getRefreshToken()
      if (!refreshToken) {
        throw new Error('No refresh token')
      }
      const tokens = await exchangeTokenFn(refreshToken)
      saveTokens(tokens.access_token, tokens.refresh_token)
      return tokens.access_token
    })().finally(() => {
      refreshPromise = null
    })

    refreshPromise = promise

    try {
      return await promise
    } catch (err) {
      clearSavedTokens()
      onUnauthenticated()
      throw err
    }
  }

  apiClient.interceptors.request.use(
    async (config) => {
      if (config.skipAuthRefresh || isOAuthTokenEndpointRequest(config)) {
        return config
      }
      const authHeaderRaw = config.headers?.get?.('Authorization') ?? config.headers?.Authorization ?? null
      const authHeader = typeof authHeaderRaw === 'string' ? authHeaderRaw : null
      const bearerMatch = authHeader?.match(/^Bearer\s+(.+)$/)
      const token = bearerMatch?.[1] ?? undefined
      if (!(token && isAccessTokenExpiredOrExpiringSoon(token))) {
        return config
      }
      const refreshToken = getRefreshToken()
      if (!refreshToken) {
        return config
      }
      const newAccessToken = await tryRefreshAndGetNewAccessToken()
      const newConfig: InternalAxiosRequestConfig = { ...config, headers: new AxiosHeaders(config.headers) }
      newConfig.headers.set('Authorization', `Bearer ${newAccessToken}`)
      return newConfig
    },
    (error) => Promise.reject(error),
  )

  apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as typeof error.config & { _retry?: boolean }
      if (
        !originalRequest ||
        error.response?.status !== 401 ||
        originalRequest._retry ||
        originalRequest.skipAuthRefresh ||
        isOAuthTokenEndpointRequest(originalRequest)
      ) {
        return Promise.reject(error)
      }
      const hadAuth = originalRequest.headers?.get?.('Authorization') ?? originalRequest.headers?.Authorization ?? null
      if (!hadAuth) {
        return Promise.reject(error)
      }
      try {
        const newAccessToken = await tryRefreshAndGetNewAccessToken()
        originalRequest._retry = true
        originalRequest.headers = originalRequest.headers ?? {}
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return apiClient.request(originalRequest)
      } catch {
        return Promise.reject(error)
      }
    },
  )
}
